import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  QrCodeIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  BuildingOffice2Icon,
  ExclamationTriangleIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';
import { useIniciarConferencia } from '../hooks/useConferencias';
import { useEmpresas } from '../hooks/usePesquisas';
import { inputIris, btnPrimario } from '../components/ui/iris';
import CameraScanner from '../components/conferencia/CameraScanner';
import { extrairChaveValida } from '../utils/chaveNfe';
import { feedbackSucesso, feedbackErro, prepararAudio } from '../utils/feedbackLeitura';
import { criarControleLeitura } from '../utils/scanCooldown';

/** Chave de acesso da NF-e: 44 dígitos numéricos (padrão SEFAZ), sempre. */
const TAMANHO_CHAVE = 44;

/** Lembra a última empresa usada no dispositivo da doca — evita reescolher a cada bipe
    (normalmente o mesmo posto de recebimento fica travado numa empresa por turno). */
const STORAGE_EMPRESA = 'conferencia-nova-empresa';

/**
 * Extrai a mensagem de erro do backend sem substituir por texto genérico.
 *
 * `POST /api/conferencia-nfe` — quando a NF-e não resolve em nenhuma fonte (ERP/manifesto),
 * o controller captura `NfeNaoResolvidaException` só neste endpoint e devolve
 * `404 { message }` com a mensagem real do `ResolvedorNfeConferencia` ("NF-e não encontrada
 * no ERP." vs "...no ERP nem no manifesto.", dependendo de `ConferenciaPermiteManifesto`) —
 * chega ao operador da doca em qualquer ambiente, sem depender do `GlobalExceptionHandler`
 * (que zera `detail` fora de Development por design, ver `backend/Middleware/
 * GlobalExceptionHandler.cs`). `detail`/`title` continuam no fallback pra outros 404 deste
 * controller que sobem pro handler global sem try/catch local (ex.: conferência/item/anexo
 * por id ausente).
 */
function mensagemErroIniciar(erro: unknown): string {
  const err = erro as {
    response?: { data?: { detail?: string; message?: string; title?: string } };
  };
  const data = err.response?.data;
  return (
    data?.detail ||
    data?.message ||
    data?.title ||
    'Não foi possível iniciar a conferência. Tente novamente.'
  );
}

const ConferenciaNovaPage: React.FC = () => {
  const navigate = useNavigate();
  const iniciar = useIniciarConferencia();
  const { data: empresasData } = useEmpresas();
  const empresas = empresasData ?? [];

  const [chave, setChave] = useState('');
  const [empresa, setEmpresa] = useState(() => localStorage.getItem(STORAGE_EMPRESA) || '1');
  const [erro, setErro] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [cameraAtiva, setCameraAtiva] = useState(false);
  // Refs (não state): mudam por leitura, não devem re-renderizar.
  const controleLeitura = useRef(criarControleLeitura()); // 1 scan físico = 1 leitura
  const iniciandoRef = useRef(false);                      // serializa: 1 iniciar por vez

  // Foco automático — a chave é bipada por leitor de código de barras na doca, sem clique.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_EMPRESA, empresa);
  }, [empresa]);

  // Se a empresa lembrada não existir mais na lista carregada, cai pra 1ª disponível em
  // vez de deixar o <select> controlado sem opção correspondente.
  useEffect(() => {
    if (empresas.length === 0) return;
    if (!empresas.some((e) => e.codigo === empresa)) {
      setEmpresa(empresas[0].codigo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresas]);

  const onChangeChave = (valor: string) => {
    // Leitores de barras às vezes emitem espaço/quebra de linha residual — mantém só dígitos.
    setChave(valor.replace(/\D/g, '').slice(0, TAMANHO_CHAVE));
    if (erro) setErro(null);
  };

  const iniciarComChave = (chaveLimpa: string) => {
    if (!chaveLimpa || iniciar.isPending) return;
    iniciar.mutate(
      { empresa: Number(empresa), chave: chaveLimpa },
      {
        onSuccess: (response) => {
          navigate(`/conferencia/${response.data.id}`);
        },
        onError: (err) => {
          // Reset do iniciandoRef para que a câmera volte a aceitar leituras após falha.
          // Sem isso, a próxima leitura será descartada e a câmera fica travada.
          iniciandoRef.current = false;
          setErro(mensagemErroIniciar(err));
          inputRef.current?.focus();
          inputRef.current?.select();
        },
      }
    );
  };

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    iniciarComChave(chave.trim());
  };

  // Cada código lido pela câmera. Só uma chave de 44 dígitos inicia; qualquer outra leitura
  // (EAN de produto, código truncado) avisa e não faz nada. `iniciandoRef` evita disparar dois
  // `iniciar` se a câmera reler a chave antes da navegação acontecer.
  const onCodigoCamera = (codigo: string) => {
    if (iniciandoRef.current || iniciar.isPending) return;
    if (!controleLeitura.current.deveAceitar(codigo, Date.now())) return;

    const chaveValida = extrairChaveValida(codigo);
    if (!chaveValida) {
      feedbackErro();
      setErro('Código lido não é uma chave de NF-e (44 dígitos). Aponte para o código de barras do DANFE.');
      return;
    }
    iniciandoRef.current = true;
    feedbackSucesso();
    setChave(chaveValida);
    setErro(null);
    iniciarComChave(chaveValida);
  };

  return (
    <div className="mx-auto max-w-[640px] space-y-4">
      <header>
        <Link
          to="/conferencia"
          className="inline-flex items-center gap-1 text-sm font-medium text-tx-3 hover:text-tx-2"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Conferências
        </Link>
        <h1 className="mt-2 flex items-center gap-2 text-xl font-semibold tracking-[-0.01em] text-tx">
          <QrCodeIcon className="h-6 w-6 flex-shrink-0 text-accent-text" />
          Iniciar conferência
        </h1>
        <p className="mt-0.5 text-sm text-tx-3">Bipe a chave de acesso da NF-e para começar.</p>
      </header>

      <div className="rounded-iris-lg border border-line bg-surface p-4 sm:p-6">
        {cameraAtiva && (
          <div className="mb-4">
            <CameraScanner onCodigo={onCodigoCamera} perfil="chave" />
          </div>
        )}
        <form onSubmit={submeter} className="space-y-4">
          {/* Empresa — select quando a pesquisa carrega; fallback numérico enquanto não
              carrega ou se a lista vier vazia (mesmo padrão de useEmpresas() usado em
              Reposição/Catálogo/Metas — não existe um seletor de "empresa atual" global
              no app, cada tela guarda a própria escolha). */}
          <div>
            <label htmlFor="empresa" className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-tx-3">
              Empresa
            </label>
            <div className="relative">
              <BuildingOffice2Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tx-3" />
              {empresas.length > 0 ? (
                <select
                  id="empresa"
                  value={empresa}
                  onChange={(e) => { setEmpresa(e.target.value); setErro(null); }}
                  disabled={iniciar.isPending}
                  className={`${inputIris} w-full pl-9`}
                >
                  {empresas.map((e) => (
                    <option key={e.codigo} value={e.codigo}>
                      {e.codigo} — {e.nomeFantasia || e.razaosocial}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id="empresa"
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={empresa}
                  onChange={(e) => { setEmpresa(e.target.value); setErro(null); }}
                  disabled={iniciar.isPending}
                  className={`${inputIris} w-full pl-9`}
                />
              )}
            </div>
          </div>

          {/* Chave de acesso — protagonista da tela. */}
          <div>
            <label htmlFor="chave" className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-tx-3">
              Chave de acesso da NF-e
            </label>
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                id="chave"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder="Bipe, digite ou leia pela câmera…"
                value={chave}
                onChange={(e) => onChangeChave(e.target.value)}
                disabled={iniciar.isPending}
                className={`${inputIris} h-14 min-w-0 flex-1 text-center text-base font-semibold tabular-nums tracking-wide sm:text-lg ${
                  erro ? 'border-danger focus:border-danger focus:ring-danger-soft' : ''
                }`}
              />
              <button
                type="button"
                onClick={() => {
                  if (!cameraAtiva) prepararAudio(); // destrava o áudio no gesto (iOS)
                  setCameraAtiva((v) => !v);
                }}
                aria-pressed={cameraAtiva}
                aria-label={cameraAtiva ? 'Desligar a câmera' : 'Ler a chave pela câmera'}
                title={cameraAtiva ? 'Desligar a câmera' : 'Ler a chave pela câmera'}
                disabled={iniciar.isPending}
                className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-iris-sm border transition-colors disabled:opacity-50 ${
                  cameraAtiva ? 'border-accent bg-accent text-white' : 'border-line-strong bg-surface text-tx-2 hover:bg-surface-2'
                }`}
              >
                <CameraIcon className="h-6 w-6" />
              </button>
            </div>
            <p className="mt-1 text-[11px] text-tx-3">{chave.length}/{TAMANHO_CHAVE} dígitos</p>
          </div>

          {erro && (
            <div className="flex items-start gap-2 rounded-iris border border-danger-border bg-danger-soft p-3">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-danger" />
              <p className="text-sm text-danger">{erro}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={!chave.trim() || iniciar.isPending}
            className={`${btnPrimario} h-12 w-full text-base`}
          >
            {iniciar.isPending ? (
              <>
                <ArrowPathIcon className="h-5 w-5 animate-spin" />
                Buscando NF-e…
              </>
            ) : (
              <>
                <QrCodeIcon className="h-5 w-5" />
                Iniciar conferência
              </>
            )}
          </button>
        </form>
      </div>

      <p className="text-center text-xs text-tx-3">
        Use o leitor de código de barras da doca ou digite a chave manualmente.
      </p>
    </div>
  );
};

export default ConferenciaNovaPage;
